/* THE COACH'S READING OF A CLIENT'S BODY METRICS — pure, so `bodyMetrics.test.ts` executes it.
 *
 * 🔴 THE COACH MAY READ THIS, AND IT IS A LIVE POLICY RATHER THAN AN ASSUMPTION.
 * `body_measurements_coach_read` (migration `20260914130000`) admits a client's ACTIVE, ACCEPTED
 * coach — his own 2026-09-14 word, tightened that day from merely `active` to require
 * `accepted_at`, the stamp only the client's own accept RPC writes. Nothing here widens that: this
 * file decides what to SAY about rows the database has already decided may be seen.
 *
 * ⚠️ AND IT REFUSES THE DEFECT IN THEIRS. Everfit's Body Metrics card prints `80 kg` with a
 * `↑ 2.6%` delta AND "No data available" in the same panel — a value beside an empty state, two
 * answers to one question. A reading here is ONE of: a value, or an honest absence. Never both.
 */
import { daysSince } from "./days";

export type Measurement = {
  /** `body_measurements.measured_at` — a timestamp, not a day. */
  measuredAtISO: string;
  weightKg: number | null;
  bodyFatPercent: number | null;
};

export type MetricReading =
  | { kind: "none"; sentence: string }
  | { kind: "one"; value: number; unit: string; whenISO: string; ageDays: number | null }
  | {
      kind: "trend";
      value: number;
      unit: string;
      whenISO: string;
      ageDays: number | null;
      /** Signed change across the window, in the metric's own unit — never a percentage. */
      change: number;
      /** The earlier reading the change is measured FROM, so the number can be checked. */
      fromValue: number;
      fromISO: string;
    };

/** Rows inside the window, newest first, that carry the metric at all. */
function inWindow(
  rows: Measurement[],
  pick: (m: Measurement) => number | null,
  today: Date,
  windowDays: number,
): { value: number; iso: string }[] {
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - windowDays);
  return rows
    .filter((m) => {
      const v = pick(m);
      if (v === null) return false;
      const t = new Date(m.measuredAtISO).getTime();
      return Number.isFinite(t) && t >= from.getTime() && t <= today.getTime();
    })
    .map((m) => ({ value: pick(m) as number, iso: m.measuredAtISO }))
    .sort((a, b) => new Date(b.iso).getTime() - new Date(a.iso).getTime());
}

/**
 * What to print for one metric over a window.
 *
 * ⚠️ ONE READING IS NOT A TREND, AND SAYING SO IS THE POINT. A single weigh-in inside four weeks
 * gives a value and no change; inventing a delta against a reading OUTSIDE the window would be a
 * different question answered in the same words. `kind: "one"` exists so the card can print the
 * number without pretending to know a direction.
 */
export function reading(
  rows: Measurement[],
  pick: (m: Measurement) => number | null,
  unit: string,
  today: Date,
  windowDays = 28,
  emptySentence = "Nothing recorded in the last four weeks.",
): MetricReading {
  const hits = inWindow(rows, pick, today, windowDays);
  const latest = hits[0];
  if (latest === undefined) return { kind: "none", sentence: emptySentence };
  const ageDays = daysSince(latest.iso.slice(0, 10), today);
  const earliest = hits[hits.length - 1];
  if (earliest === undefined || earliest.iso === latest.iso) {
    return { kind: "one", value: latest.value, unit, whenISO: latest.iso, ageDays };
  }
  return {
    kind: "trend",
    value: latest.value,
    unit,
    whenISO: latest.iso,
    ageDays,
    // ⚠️ THE UNIT, NOT A PERCENTAGE. "−1.5 kg" is checkable against the two readings printed
    // beside it; "−2.6%" hides both of them and cannot be reconciled with anything on screen.
    change: Math.round((latest.value - earliest.value) * 100) / 100,
    fromValue: earliest.value,
    fromISO: earliest.iso,
  };
}

/** "13 days ago" / "today" — the same register the rest of the page uses. */
export function whenSaid(ageDays: number | null): string {
  if (ageDays === null) return "date unreadable";
  if (ageDays <= 0) return "today";
  if (ageDays === 1) return "yesterday";
  return `${ageDays} days ago`;
}
